import { useState, useRef, useCallback, useEffect } from 'react';
import { capture } from '@/lib/analytics';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for consuming the SSE build stream endpoint.
 * Returns real-time step progress, preview pages, and final magic state.
 */
export default function useBuildStream(projectId) {
  const [steps, setSteps] = useState([]);
  const [thinkingMessages, setThinkingMessages] = useState([]);
  const [previewPages, setPreviewPages] = useState([]);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [magicState, setMagicState] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const eventSourceRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const isCompleteRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startBuild = useCallback(() => {
    // Prevent double-start: if already building or an EventSource is open, skip
    if (eventSourceRef.current && eventSourceRef.current.readyState !== 2) {
      console.warn('[useBuildStream] Build already in progress — ignoring duplicate startBuild');
      return;
    }
    // Reset state
    setSteps([]);
    setThinkingMessages([]);
    setPreviewPages([]);
    setIsBuilding(true);
    setIsComplete(false);
    isCompleteRef.current = false;
    setError(null);
    setMagicState(null);
    setElapsedMs(0);

    // Start elapsed timer
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 100);

    // Get JWT token
    const token = localStorage.getItem('orbis_token');
    if (!token) {
      setError('Not authenticated');
      setIsBuilding(false);
      clearInterval(timerRef.current);
      return;
    }

    capture('magic_build_started', { project_id: projectId });

    const url = `${API_URL}/magic/projects/${projectId}/build/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.addEventListener('step:start', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => {
        // Update if step already exists (for parallel pages), otherwise add
        const existing = prev.find(s => s.id === data.step_id);
        if (existing) {
          return prev.map(s => s.id === data.step_id ? { ...s, status: 'running', startTime: Date.now() } : s);
        }
        return [...prev, {
          id: data.step_id,
          label: data.label,
          description: data.description,
          index: data.index,
          totalSteps: data.total_steps,
          status: 'running',
          duration_ms: null,
          startTime: Date.now()
        }];
      });
    });

    es.addEventListener('step:complete', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => prev.map(s =>
        s.id === data.step_id
          ? { ...s, status: 'done', duration_ms: data.duration_ms }
          : s
      ));
    });

    es.addEventListener('step:error', (e) => {
      const data = JSON.parse(e.data);
      setSteps(prev => prev.map(s =>
        s.id === data.step_id
          ? { ...s, status: 'error', errorMsg: data.error }
          : s
      ));
      capture('build_error', { project_id: projectId, step_id: data.step_id, error: data.error, has_fallback: !!data.fallback });
      if (!data.fallback) {
        setError(data.error);
        setIsBuilding(false);
        clearInterval(timerRef.current);
      }
    });

    es.addEventListener('thinking', (e) => {
      const data = JSON.parse(e.data);
      setThinkingMessages(prev => [...prev, {
        stepId: data.step_id,
        message: data.message,
        timestamp: Date.now()
      }]);
    });

    es.addEventListener('preview:update', (e) => {
      const data = JSON.parse(e.data);
      setPreviewPages(prev => {
        const existing = prev.findIndex(p => p.slug === data.page_slug);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { slug: data.page_slug, name: data.page_name, html: data.html, css: data.css, js: data.js };
          return updated;
        }
        return [...prev, { slug: data.page_slug, name: data.page_name, html: data.html, css: data.css, js: data.js }];
      });
    });

    es.addEventListener('build:complete', (e) => {
      const data = JSON.parse(e.data);
      capture('magic_build_complete', { project_id: projectId, elapsed_ms: Date.now() - startTimeRef.current });
      isCompleteRef.current = true;
      setMagicState(data.magic_state);
      setIsComplete(true);
      setIsBuilding(false);
      clearInterval(timerRef.current);
      es.close();
    });

    es.onerror = () => {
      // EventSource fires onerror on real network failures AND normal server close.
      // Delay so build:complete handler sets isCompleteRef first.
      setTimeout(() => {
        if (isCompleteRef.current) {
          es.close();
          return;
        }
        // readyState CLOSED = server terminated; CONNECTING = transient, will auto-retry
        if (es.readyState === EventSource.CLOSED) {
          setError('Connection lost during build. Check server logs for details.');
          setIsBuilding(false);
          clearInterval(timerRef.current);
          es.close();
        }
        // If CONNECTING, EventSource auto-retries — don't kill it
      }, 1500);
    };
  }, [projectId]);

  const abortBuild = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setIsBuilding(false);
  }, []);

  return {
    steps,
    thinkingMessages,
    previewPages,
    isBuilding,
    isComplete,
    error,
    magicState,
    elapsedMs,
    startBuild,
    abortBuild,
  };
}
